import useEditShopContext from 'contexts/shop.context';
import Map from 'components/map';
import { LatLngTuple } from 'leaflet';
import { DeleteIcon, EditAltIcon } from 'cokitchen-icons';
import React, { useEffect, useState } from 'react';
import PolygonModal, { PolygonModalType } from 'components/shop-polygon-modal';
import PolygonCard from 'components/shop-polygon-card/index';
import { PolygonItemType } from 'components/polygon-card/polygon-item';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import DeleteModal from 'components/base/delete-modal';
import { ShopPolygonType } from 'utils/types';
import { useParams } from 'react-router-dom';
type Props = {
  onEditComplete: () => void;
};
const EditShopPolygon = ({ onEditComplete }: Props) => {
  const { setShopDetails, shopDetails, polygons, setPolygons, getShopById } = useEditShopContext();

  const [showPolygonModal, setShowPolygonModal] = useState(false);
  const [polygon, setPolygon] = useState<LatLngTuple[]>();
  const [polygonModalType, setPolygonModalType] = useState<PolygonModalType>('edit');
  const [showDeletePolygonModal, setShowDeletePolygonModal] = useState(false);
  const [newPolygon, setnewPolygon] = useState<ShopPolygonType | null>(null);
  const [activePolygon, setActivePolygon] = useState<ShopPolygonType | null>(null);
  const { shop_id } = useParams();

  const togglePolygonModal = () => {
    setShowPolygonModal(!showPolygonModal);
  };

  const editPolygon = () => {
    togglePolygonModal();
    setPolygonModalType('edit');
  };

  const handleEditShopPolygon = async (payload: any) => {
    const previousPolygons = [...polygons];
    if (!shop_id) {
      const updatedPolygons = polygons.map((poly) =>
        poly.id === payload.id ? { ...poly, ...payload } : poly
      );
      setPolygons(updatedPolygons);
      setShopDetails({
        ...shopDetails,
        shop_polygons: updatedPolygons
      });

      setShowPolygonModal(false);
      return;
    }

    try {
      const res = await apiService.put(`shops/polygons/${payload.id}`, { data: payload });
      if (res?.data) {
        const updatedPolygons = polygons.map((poly) =>
          poly.id === payload.id ? { ...poly, ...payload } : poly
        );

        setPolygons(updatedPolygons);
        setShopDetails({
          ...shopDetails,
          shop_polygons: updatedPolygons
        });

        toast(<ToastMessage type='success' message={`${payload?.name} updated successfully`} />);
        setShowPolygonModal(false);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
      setPolygons(previousPolygons);
    }
  };

  const handleDeletePolygon = async () => {
    try {
      const updatedPolygons = polygons.filter((polygon) => polygon.id !== activePolygon?.id);

      setPolygons(updatedPolygons);
      if (shop_id) {
        const res: any = await apiService.delete(`shops/polygons/${activePolygon?.id}`);
        if (res.data) {
          toast(<ToastMessage type='success' message='Polygon deleted successfully' />);
          setShowDeletePolygonModal(false);
        }
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
      setPolygons(polygons);
    } finally {
      setShowDeletePolygonModal(false);
    }
  };

  const addShopPolygon = async (polygon: ShopPolygonType) => {
    if (!polygon) return;

    if (shop_id) {
      try {
        const payload = {
          data: {
            shop_id: shop_id,
            shop_polygons: [polygon]
          }
        };

        const res = await apiService.post('shops/polygons', payload);
        if (res?.data) {
          toast(
            <ToastMessage type='success' message={`${newPolygon?.name} created successfully`} />
          );

          getShopById();
        }
      } catch (err: any) {
        toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
      } finally {
        onEditComplete();
      }
    }

    setShopDetails({
      ...shopDetails,
      shop_polygons: [...shopDetails.shop_polygons, polygon]
    });

    setShowPolygonModal(false);
  };

  useEffect(() => {
    setPolygon(activePolygon?.polygon as LatLngTuple[]);
  }, [activePolygon]);

  const POLYGON_MENU: PolygonItemType[] = [
    {
      name: 'Edit Polygon',
      icon: <EditAltIcon />,
      onClick: editPolygon
    },
    {
      name: 'Delete Polygon',
      icon: <DeleteIcon className='text-red-5' />,
      onClick: () => setShowDeletePolygonModal(true),
      className: 'text-red-5'
    }
  ];

  return (
    <section className={`space-y-8 $`}>
      <div>
        <div className='flex justify-between'>
          <h4 className='font-semibold text-2xl'>Manage FoodCourts delivery locations</h4>
          <button
            className=' font-semibold text-red-5 hover:text-red-6  h-[40px] py-2 rounded-[8px]'
            onClick={() => {
              togglePolygonModal();
              setPolygonModalType('create');
            }}>
            + New Polygon
          </button>
        </div>
        <div className='my-2 text-gray-5'>
          <h3>Create new delivery polygons and expand the reach of our current locations</h3>
        </div>
        <div className='flex flex-col gap-6 sm:justify-center sm:flex-row'>
          <div className='flex-1'>
            <Map type={'edit'} positions={polygon} />
          </div>
          <PolygonModal
            show={showPolygonModal}
            onClose={() => {
              togglePolygonModal();
            }}
            positions={polygon as LatLngTuple[]}
            data={{
              name: activePolygon?.name ?? ''
            }}
            type={polygonModalType}
            setnewPolygon={(poly) => {
              setnewPolygon(poly);
              addShopPolygon(poly);
            }}
            polygons={polygons}
            activePolygon={activePolygon}
            onEdit={handleEditShopPolygon}
          />
          {polygons?.length > 0 && (
            <div className='flex-1'>
              <PolygonCard
                list={POLYGON_MENU}
                polygons={polygons}
                setActivePolygon={setActivePolygon}
              />
            </div>
          )}

          <DeleteModal
            show={showDeletePolygonModal}
            onClose={() => setShowDeletePolygonModal(false)}
            handleDelete={handleDeletePolygon}
            description='This action cannot be undone'
            title={`Are you sure you want to delete this polygon`}
          />
        </div>
      </div>
    </section>
  );
};

export default EditShopPolygon;
